import { drawGate, type ZoneLine } from '@urbanrats/domain';
import { useEffect, useRef, useState } from 'react';

type Box = [id: number, x1: number, y1: number, x2: number, y2: number, cls: string];
interface Sidecar {
  seq: number;
  startMs: number;
  durationMs: number;
  frames: [offsetMs: number, boxes: Box[]][];
  crossings: { eventId: string; trackId: string; wallMs: number; direction: string; vehicleClass: string }[];
}
interface Relay {
  playlistUrl: string;
  line: { a: { x: number; y: number }; b: { x: number; y: number }; curve?: { x: number; y: number }; zone?: { a: { x: number; y: number }; b: { x: number; y: number } }; countedDirection: string };
  /** A round's window (its "+1" only inside it); absent for a camera relay (every counted crossing shows). */
  timing?: { captureStartAt: string; captureEndAt: string };
}

const COLOURS = ['#4cc9f0', '#3ddc84', '#ffb347', '#a78bfa', '#ff7eb6', '#ffd21f', '#7dd3fc', '#f97316'];

/**
 * Operations view of a live round: the same relay players get (our counted segments), with the counting line,
 * the counter's track boxes and its counted crossings. Read-only; the count shown is the counter's.
 */
export function AdminRelay({ roundId, cameraId, apiBase, post, muted = true, onPicture, compact = false }: { roundId?: string; cameraId?: string; compact?: boolean; apiBase: string; post: <T>(path: string, body: Record<string, unknown>) => Promise<{ ok: true; value: T } | { ok: false; error: string }>; muted?: boolean; onPicture?: (p: { wallMs: number | null; count: number }) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [relay, setRelay] = useState<Relay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [behind, setBehind] = useState<number | null>(null);
  const segs = useRef(new Map<number, Sidecar>());
  const hits = useRef(new Map<string, { wallMs: number; counted: boolean; x: number; y: number }>());
  const hlsRef = useRef<import('hls.js').default | null>(null);
  const onPic = useRef(onPicture);
  useEffect(() => {
    onPic.current = onPicture;
  });

  useEffect(() => {
    let live = true;
    segs.current.clear();
    hits.current.clear();
    const path = cameraId ? `/v1/admin/live/cameras/${encodeURIComponent(cameraId)}/relay` : `/v1/admin/live/rounds/${encodeURIComponent(roundId ?? '')}/relay`;
    void post<Relay>(path, {}).then((r) => {
      if (!live) return;
      if (r.ok) setRelay(r.value);
      else setError(r.error);
    });
    return () => {
      live = false;
    };
  }, [roundId, cameraId, post]);

  useEffect(() => {
    const v = video.current;
    if (!v || !relay) return;
    let dead = false;
    let hls: import('hls.js').default | null = null;
    const url = `${apiBase}${relay.playlistUrl}`;
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      if (!Hls.isSupported()) {
        v.src = url;
        return;
      }
      hls = new Hls({ lowLatencyMode: true, liveSyncDuration: 9, maxBufferLength: 20 });
      hlsRef.current = hls;
      hls.on(Hls.Events.FRAG_LOADED, (_e, d) => {
        const f = d.frag;
        if (typeof f.sn !== 'number') return;
        const sn = f.sn;
        void fetch(f.url.replace(/\/(\d+)\.(ts|m4s)\?/, '/$1.json?'))
          .then((r) => (r.ok ? (r.json() as Promise<Sidecar>) : null))
          .then((side) => {
            if (dead || !side) return;
            segs.current.set(sn, side);
            for (const c of side.crossings) {
              // Where the vehicle was at the crossing: the frame nearest to it.
              const off = c.wallMs - side.startMs;
              let best: [number, Box[]] | null = null;
              for (const fr of side.frames) if (!best || Math.abs(fr[0] - off) < Math.abs(best[0] - off)) best = fr;
              const tid = Number(String(c.trackId).replace(/^t/, ''));
              const b = best?.[1].find((x) => x[0] === tid);
              hits.current.set(c.eventId, { wallMs: c.wallMs, counted: c.direction === relay.line.countedDirection, x: b ? (b[1] + b[3]) / 2 : 0.5, y: b ? b[2] : 0.5 });
            }
            for (const [k, s] of segs.current) if (s.startMs < side.startMs - 90_000) segs.current.delete(k);
          })
          .catch(() => undefined);
      });
      hls.on(Hls.Events.ERROR, (_e, d) => {
        if (!d.fatal) return;
        if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls?.recoverMediaError();
        else setError('relay interrupted');
      });
      hls.on(Hls.Events.MANIFEST_PARSED, () => void v.play().catch(() => undefined));
      hls.loadSource(url);
      hls.attachMedia(v);
    });
    return () => {
      dead = true;
      hls?.destroy();
      hlsRef.current = null;
    };
  }, [relay, apiBase]);

  useEffect(() => {
    let raf = 0;
    let tick = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const v = video.current;
      const cv = canvas.current;
      if (!v || !cv || !relay || !v.videoWidth) return;
      const w = v.videoWidth;
      const h = v.videoHeight;
      if (cv.width !== w || cv.height !== h) {
        cv.width = w;
        cv.height = h;
      }
      const g = cv.getContext('2d')!;
      g.clearRect(0, 0, w, h);
      const px = Math.max(2, Math.round(w / 420));
      const wall = hlsRef.current?.playingDate?.getTime() ?? null;
      const start = relay.timing ? Date.parse(relay.timing.captureStartAt) : -Infinity;
      const end = relay.timing ? Date.parse(relay.timing.captureEndAt) : Infinity;
      const inWindow = wall !== null && wall >= start && wall < end;
      // The counting gate exactly as players see it (zone, chevrons, bold line; white flash on a counted vehicle).
      let flash = 0;
      if (wall !== null)
        for (const hit of hits.current.values())
          if (hit.counted && hit.wallMs >= start && hit.wallMs < end && wall >= hit.wallMs && wall - hit.wallMs < 350) flash = Math.max(flash, 1 - (wall - hit.wallMs) / 350);
      drawGate(g, relay.line as ZoneLine, w, h, { px, now: performance.now() / 1000, dim: !inWindow, flash });
      // boxes of the frame on screen
      if (wall !== null) {
        for (const s of segs.current.values()) {
          if (wall < s.startMs || wall >= s.startMs + s.durationMs) continue;
          const off = wall - s.startMs;
          let fr: [number, Box[]] | null = null;
          for (const f of s.frames) if (f[0] <= off) fr = f;
          if (!fr) break;
          g.lineWidth = px;
          for (const b of fr[1]) {
            g.strokeStyle = COLOURS[b[0] % COLOURS.length]!;
            g.strokeRect(b[1] * w, b[2] * h, (b[3] - b[1]) * w, (b[4] - b[2]) * h);
          }
          break;
        }
      }
      // "+1" of counted crossings inside the window, for a moment after they happen on screen
      if (wall !== null) {
        const font = Math.round(h / 14);
        g.font = `800 ${font}px 'Avenir Next Condensed', 'Arial Narrow', sans-serif`;
        g.textAlign = 'center';
        for (const x of hits.current.values()) {
          const dt = (wall - x.wallMs) / 1000;
          if (!x.counted || x.wallMs < start || x.wallMs >= end || dt < 0 || dt > 1.2) continue;
          const a = 1 - dt / 1.2;
          g.globalAlpha = a;
          g.lineWidth = Math.max(3, font / 6);
          g.strokeStyle = 'rgba(0,0,0,0.85)';
          g.strokeText('+1', x.x * w, x.y * h - dt * font);
          g.fillStyle = '#ffd21f';
          g.fillText('+1', x.x * w, x.y * h - dt * font);
        }
        g.globalAlpha = 1;
      }
      if (performance.now() - tick > 250) {
        tick = performance.now();
        setBehind(wall !== null ? Math.max(0, Math.round((Date.now() - wall) / 1000)) : null);
        // The count as the picture shows it: counted crossings of the window up to the frame on screen.
        let n = 0;
        if (wall !== null) for (const x of hits.current.values()) if (x.counted && x.wallMs >= start && x.wallMs < end && x.wallMs <= wall) n++;
        onPic.current?.({ wallMs: wall, count: n });
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [relay]);

  return (
    <div className="relay" data-testid="admin-relay">
      <video ref={video} muted={muted} playsInline autoPlay />
      <canvas ref={canvas} />
      {error && <div className="relay-msg">{error}</div>}
      {!error && !relay && <div className="relay-msg">Connecting to the relay…</div>}
      {behind !== null && !compact && <span className="relay-behind">picture {behind} s behind real time</span>}
    </div>
  );
}
